// src/useAddToHomeScreen.js
import { useEffect, useState, useCallback } from "react";

export default function useAddToHomeScreen() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState("unknown");
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isiOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    const isFirefox = /firefox|fxios/.test(ua);
    const isChromium = /chrome|crios|edg/.test(ua) && !/opr|opera/.test(ua);
    const isMac = /macintosh/.test(ua);
    const isSafari =
      /safari/.test(ua) &&
      !/chrome|crios|edg|opr|android|fxios/.test(ua);

    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    setIsStandalone(Boolean(standalone));

    if (isiOS) setPlatform("ios");
    else if (isMac && isSafari) setPlatform("mac-safari");
    else if (isFirefox && isAndroid) setPlatform("ff-android");
    else if (isFirefox) setPlatform("ff-desktop");
    else if (isChromium) setPlatform("chromium");
    else setPlatform("other");

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const canPromptNative = Boolean(deferredPrompt);

  const triggerInstall = useCallback(async () => {
    // ✅ Works only on Chromium
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const res = await deferredPrompt.userChoice;
      setTimeout(() => setDeferredPrompt(null), 0);
      return res;
    }

    // Manual instructions
    switch (platform) {
      case "ios":
        return {
          outcome: "manual",
          message: "On iOS Safari: Tap Share → Add to Home Screen",
        };
      case "mac-safari":
        return {
          outcome: "manual",
          message: "On macOS Safari: Go to Safari menu → File → Add to Dock",
        };
      case "ff-android":
        return {
          outcome: "manual",
          message: "On Firefox Android: Open menu → Install",
        };
      case "ff-desktop":
        return {
          outcome: "manual",
          message: "Firefox desktop does not support install. Use another browser.",
        };
      default:
        return {
          outcome: "manual",
          message: "Your browser does not support automatic install. Please use Add to Home Screen from the browser menu.",
        };
    }
  }, [deferredPrompt, platform]);

  return {
    platform,
    isStandalone,
    canPromptNative,
    triggerInstall,
  };
}
